import React, { useEffect, useRef, useState } from 'react';
import { Table, Form, Input, Button, Select, DatePicker, Row, Col, Modal, Typography, message, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import masterService from '../../../services/masterService';
const { RangePicker } = DatePicker;

// Minimum age for a user account. The latest acceptable date of birth is this many
// years before today, so anyone born after it is under age.
export const MIN_AGE_YEARS = 18;

// Computed per call rather than once at module load: the dashboard is a long-lived
// tab, and a session open across midnight would otherwise keep yesterday's cutoff.
// dayjs handles the leap-year case (29 Feb minus 18 years) that naive arithmetic
// gets wrong.
export const latestAllowedDob = () => dayjs().subtract(MIN_AGE_YEARS, 'year').endOf('day');

// The single rule behind both the picker's greyed-out dates and the form's
// validator. Exported so it can be tested directly: antd's calendar panel does not
// render under jsdom, so driving the picker in a test proves nothing.
export const isUnderageDob = (date) => !!date && dayjs(date).isAfter(latestAllowedDob());

const statusOptions = [
  { label: 'TRIAL', value: 'TRIAL' },
  { label: 'ACTIVE', value: 'ACTIVE' },
  { label: 'EXPIRED', value: 'EXPIRED' },
  { label: 'CANCELLED', value: 'CANCELLED' },
];

// Sort params for the API (empty when no active sort)
const sortParams = (s) =>
  s.field && s.order
    ? { sortBy: s.field, sortOrder: s.order === 'ascend' ? 'asc' : 'desc' }
    : {};


function UserSubscription() {
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [planOptions, setPlanOptions] = useState([]);
  const [planTypeOptions, setPlanTypeOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  // Server-side sort state ({ field, order } with antd's ascend/descend)
  const [sortState, setSortState] = useState({ field: null, order: null });
  const [invoiceModal, setInvoiceModal] = useState({ visible: false, loading: false, invoice: null, userId: null, paymentId: null });

  // Assign-subscription modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignForm] = Form.useForm();
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const userSearchTimerRef = useRef(null);
  // Active-only plans/types for the assign modal (separate from the page-level
  // filter dropdowns, which can show any plan/type used by an existing sub).
  const [assignPlanOptions, setAssignPlanOptions] = useState([]);
  const [assignPlanTypeOptions, setAssignPlanTypeOptions] = useState([]);
  const [assignCountryOptions, setAssignCountryOptions] = useState([]);
  // The selected user's inherited country when it is not in the active list
  // (inactive country) — still shown and submittable, per product decision.
  const [inheritedCountryOption, setInheritedCountryOption] = useState(null);

  // Add-user modal, opened from the assign modal's header. It exists so a
  // subscription can be assigned to someone who has never signed up: on success
  // the new user is selected in the assign form straight away, so the admin never
  // has to go and search for the account they just created.
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [addUserForm] = Form.useForm();
  const [addUserSubmitting, setAddUserSubmitting] = useState(false);
  // Dial codes come from the backend (m_country, via getCountryMobileCodes) alongside
  // the assign options, so this list is not a second hardcoded copy. The same feed now
  // backs the create-user validation server-side, so the codes offered here are exactly
  // the codes that will be accepted.
  const [mobileCodeOptions, setMobileCodeOptions] = useState([]);

  // Show invoice info modal for a specific user and payment

  const handleCloseInvoiceModal = () => {
    setInvoiceModal({ visible: false, loading: false, invoice: null, userId: null, paymentId: null });
  };

  const handleOpenAssignModal = async () => {
    assignForm.resetFields();
    setUserOptions([]);
    setInheritedCountryOption(null);
    setAssignModalOpen(true);
    // Pre-load active plans + plan types from the new endpoint (search-less call).
    try {
      const res = await masterService.getAssignSubscriptionOptions({});
      const data = res?.data?.data ?? {};
      setAssignPlanOptions(
        (data.subscription_plans || []).map((p) => ({ label: p.title, value: p.id }))
      );
      setAssignPlanTypeOptions(
        (data.plan_types || []).map((t) => ({ label: t.name, value: t.id }))
      );
      setAssignCountryOptions(
        (data.countries || []).map((c) => ({
          label: c.country_name,
          value: String(c.country_id),
        }))
      );
      // Phone dial codes for the Add User form — a different list from
      // `countries` above, which is the billing-country master.
      setMobileCodeOptions(
        (data.mobile_country_codes || []).map((c) => ({
          label: `${c.code} (${c.iso})`,
          value: c.code,
        }))
      );
    } catch (e) {
      console.error('Error loading assign-subscription options', e);
      message.error('Failed to load plans / plan types');
    }
  };

  const handleCloseAssignModal = () => {
    if (assignSubmitting) return;
    setAssignModalOpen(false);
  };

  // Debounced user lookup against the dedicated assign-options endpoint, which
  // returns distinct (verified + active + non-deleted) users — no duplicates
  // from joined subscriptions.
  const handleUserSearch = (value) => {
    const term = (value || '').trim();
    if (userSearchTimerRef.current) {
      clearTimeout(userSearchTimerRef.current);
    }
    if (!term) {
      setUserOptions([]);
      return;
    }
    userSearchTimerRef.current = setTimeout(async () => {
      setUserSearchLoading(true);
      try {
        const res = await masterService.getAssignSubscriptionOptions({
          search: term,
          perPage: 20,
        });
        const users = res?.data?.data?.users ?? [];
        setUserOptions(
          users.map((u) => ({
            label: `${u.user_email}${
              u.user_firstname
                ? ` — ${u.user_firstname}${u.user_lastname ? ' ' + u.user_lastname : ''}`
                : ''
            }`,
            value: u.user_id,
            subscription_country_id: u.subscription_country_id ?? null,
            subscription_country_name: u.subscription_country_rel?.country_name ?? null,
            subscription_country_is_active: u.subscription_country_rel?.country_is_active ?? null,
          }))
        );
      } catch (e) {
        console.error('Error searching users', e);
        message.error('Failed to search users');
      } finally {
        setUserSearchLoading(false);
      }
    }, 300);
  };

  const handleOpenAddUserModal = () => {
    addUserForm.resetFields();
    setAddUserModalOpen(true);
  };

  const handleCloseAddUserModal = () => {
    if (addUserSubmitting) return;
    setAddUserModalOpen(false);
  };

  const handleAddUserSubmit = async (values) => {
    setAddUserSubmitting(true);
    try {
      const payload = {
        user_firstname: values.user_firstname?.trim(),
        user_lastname: values.user_lastname?.trim(),
        user_email: values.user_email?.trim(),
        // Required by the form, so sent unconditionally — the picker holds a dayjs
        // object and the API parses the ISO date it formats to.
        user_dob: values.user_dob.format('YYYY-MM-DD'),
        user_gender: values.user_gender,
        // Mobile and its dial code are required too, so both are sent outright. They
        // used to be spread in only when filled, which meant a blank pair was quietly
        // omitted and the account was created with no phone identity at all — the API
        // now rejects that, so omitting them would only turn a caught mistake into a
        // failed request.
        user_country_code: values.user_country_code,
        user_mobile: values.user_mobile?.trim(),
      };
      const res = await masterService.createWebsiteUser(payload);
      if (res?.data?.status) {
        const created = res?.data?.data ?? {};
        message.success(res?.data?.message || 'User created');

        // Select the new user in the assign form immediately — the whole point of
        // creating them here. The option is pushed in by hand because the user
        // list is search-driven and this account matches no search yet.
        if (created.user_id) {
          const label = `${created.user_email}${
            created.user_firstname
              ? ` — ${created.user_firstname}${created.user_lastname ? ' ' + created.user_lastname : ''}`
              : ''
          }`;
          setUserOptions([
            {
              label,
              value: created.user_id,
              subscription_country_id: created.subscription_country_id ?? null,
              subscription_country_name: null,
              subscription_country_is_active: null,
            },
          ]);
          assignForm.setFieldsValue({ user_id: created.user_id });
          // A brand-new user has no billing country yet, so the country field is
          // left for the admin to choose rather than being cleared silently.
          setInheritedCountryOption(null);
        }
        setAddUserModalOpen(false);
      } else {
        message.error(res?.data?.message || 'Failed to create user');
      }
    } catch (e) {
      console.error('Error creating user', e);
      // The response interceptor rejects with `error.response`, not the axios error
      // (see services/api.js), so the body is at e.data — reading e.response.data
      // here is one level too deep, resolves to undefined, and threw away every
      // message the API sends: which field is missing, that the dial code is not
      // supported, that the number already belongs to someone. The admin saw only
      // "Failed to create user" and had nothing to act on.
      message.error(e?.data?.message || 'Failed to create user');
    } finally {
      setAddUserSubmitting(false);
    }
  };

  const handleAssignSubmit = async (values) => {
    setAssignSubmitting(true);
    try {
      const payload = {
        user_id: values.user_id,
        plan_id: values.plan_id,
        plan_type_id: values.plan_type_id,
        ...(values.country_id ? { country_id: Number(values.country_id) } : {}),
      };
      const res = await masterService.assignUserSubscription(payload);
      if (res?.data?.status) {
        message.success(res?.data?.message || 'Subscription assigned');
        setAssignModalOpen(false);
        fetchData({ current: 1, filters: buildFilters(form.getFieldsValue()) });
      } else {
        message.error(res?.data?.message || 'Failed to assign subscription');
      }
    } catch (e) {
      console.error('Error assigning subscription', e);
      message.error(e?.response?.data?.message || 'Failed to assign subscription');
    } finally {
      setAssignSubmitting(false);
    }
  };

  const handleDownloadInvoice = async (userId, paymentId) => {
    // Open the tab synchronously within the click gesture; opening it after
    // the await gets blocked as a popup by the browser.
    const win = window.open('', '_blank');
    try {
      const response = await masterService.downloadUserInvoice(userId, paymentId);
      if (response.data && response.data.status && response.data.invoice_url) {
        if (win) {
          win.location.href = response.data.invoice_url;
        } else {
          window.open(response.data.invoice_url, '_blank');
        }
      } else {
        if (win) win.close();
        message.info(response.data.message || 'Invoice not available');
      }
    } catch (err) {
      if (win) win.close();
      message.error('Error fetching invoice');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', sorter: true },
    { title: 'User ID', dataIndex: 'fk_user_id', key: 'fk_user_id', sorter: true },
    { title: 'Plan Name', dataIndex: 'plan_name', key: 'plan_name' },
    { title: 'Plan Type Name', dataIndex: 'plan_type_name', key: 'plan_type_name' },
    { title: 'Country Name', dataIndex: 'country_name', key: 'country_name' },
    { title: 'Payment ID', dataIndex: 'fk_payment_id', key: 'fk_payment_id', sorter: true },
    { title: 'Status', dataIndex: 'subscription_status', key: 'subscription_status', sorter: true },
    { title: 'Start', dataIndex: 'subscription_start', key: 'subscription_start', sorter: true, render: v => v && new Date(v).toLocaleString() },
    { title: 'End', dataIndex: 'subscription_end', key: 'subscription_end', sorter: true, render: v => v && new Date(v).toLocaleString() },
    { title: 'Is Autopay', dataIndex: 'is_autopay', key: 'is_autopay', render: v => (v ? 'Yes' : 'No') },
    { title: 'Is Active', dataIndex: 'is_active', key: 'is_active', render: v => (v ? 'Yes' : 'No') },
    { title: 'Is Deleted', dataIndex: 'is_deleted', key: 'is_deleted', render: v => (v ? 'Yes' : 'No') },
    { title: 'Assigned by Admin', dataIndex: 'admin_assigned', key: 'admin_assigned', render: v => (v ? 'Yes' : 'No') },
    { title: 'Admin ID', dataIndex: 'assigned_by_admin_id', key: 'assigned_by_admin_id', render: v => v ?? '-' },
    { title: 'Admin Name', dataIndex: 'assigned_admin_name', key: 'assigned_admin_name', render: v => v || '-' },
    { title: 'Created At', dataIndex: 'created_at', key: 'created_at', sorter: true, render: v => v && new Date(v).toLocaleString() },
    {
      title: 'Invoice',
      key: 'invoice',
      render: (_, record) => (
        <Button
          type="primary"
          onClick={() => handleDownloadInvoice(record.fk_user_id, record.fk_payment_id)}
        >
          Download
        </Button>
      ),
    },
  ];

  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      const { data: res } = await masterService.getUserSubscriptionList({
        page: params.current || pagination.current,
        pageSize: params.pageSize || pagination.pageSize,
        ...params.filters,
      });
      // Map nested fields to flat fields for table
      const mappedData = (res.data || []).map(item => ({
        ...item,
        plan_name: item.subscription_plan?.title || '',
        plan_type_name: item.m_plan_type?.name || '',
        country_name: item.m_country?.country_name || '',
      }));
      setData(mappedData);
      setPlanOptions((res.subscription_plan || []).map(item => ({
        label: item.title,
        value: String(item.id),
      })));
      setPlanTypeOptions((res.m_plan_type || []).map(item => ({
        label: item.name,
        value: String(item.id),
      })));
      setCountryOptions((res.m_country || []).map(item => ({
        label: item.country_name,
        value: String(item.country_id),
      })));
      setPagination({
        current: res.page,
        pageSize: res.pageSize,
        total: res.total,
      });
    } catch (err) {
      console.error('Error fetching user subscriptions', err);
      message.error('Failed to fetch user subscriptions');
      setData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  const buildFilters = (values = {}) => {
    const filters = { ...values };

    for (const key of ['user']) {
      if (filters[key] !== undefined) {
        const v = String(filters[key]).trim();
        if (v) filters[key] = v;
        else delete filters[key];
      }
    }

    if (filters.dateRange) {
      filters.startDate = filters.dateRange[0]?.format('YYYY-MM-DD');
      filters.endDate = filters.dateRange[1]?.format('YYYY-MM-DD');
      delete filters.dateRange;
    }

    return filters;
  };

  const onFinish = values => {
    fetchData({ current: 1, filters: { ...buildFilters(values), ...sortParams(sortState) } });
  };

  const handleTableChange = (pag, tableFilters, sorter) => {
    // 3-state column sort (asc → desc → normal), applied server-side
    const nextSort = sorter?.order
      ? { field: sorter.field, order: sorter.order }
      : { field: null, order: null };
    const sortChanged =
      nextSort.field !== sortState.field || nextSort.order !== sortState.order;
    setSortState(nextSort);
    fetchData({
      current: sortChanged ? 1 : pag.current,
      pageSize: pag.pageSize,
      filters: { ...buildFilters(form.getFieldsValue()), ...sortParams(nextSort) },
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenAssignModal}
          >
            Assign Subscription
          </Button>
        </Space>
      </div>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        style={{ marginBottom: 16, background: '#fff', padding: 16, borderRadius: 8 }}
      >
        <Row gutter={16}>
          <Col span={4}><Form.Item name="user" label="User ID"><Input allowClear /></Form.Item></Col>
          <Col span={4}><Form.Item name="planId" label="Plan Name"><Select allowClear options={planOptions} /></Form.Item></Col>
          <Col span={4}><Form.Item name="planTypeId" label="Plan Type Name"><Select allowClear options={planTypeOptions} /></Form.Item></Col>
          <Col span={4}><Form.Item name="status" label="Status"><Select allowClear options={statusOptions} /></Form.Item></Col>
          <Col span={6}><Form.Item name="dateRange" label="Subscription Date"><RangePicker /></Form.Item></Col>
          <Col span={6}><Form.Item name="countryId" label="Country Name"><Select allowClear options={countryOptions} /></Form.Item></Col>
          <Col span={3}><Form.Item name="isActive" label="Is Active"><Select allowClear options={[{ label: 'Yes', value: 'true' }, { label: 'No', value: 'false' }]} /></Form.Item></Col>
          <Col span={3}><Form.Item name="isDeleted" label="Is Deleted"><Select allowClear options={[{ label: 'Yes', value: 'true' }, { label: 'No', value: 'false' }]} /></Form.Item></Col>
        </Row>
        <Row>
          <Col span={4} style={{ marginTop: 8 }}>
            <Form.Item>
              <Button type="primary" htmlType="submit" >Filter</Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={pagination}
        rowKey="id"
        onChange={handleTableChange}
        bordered
        size="middle"
        scroll={{ x: 'max-content' }}
      />
      <Modal
        // Title is a flex row so "Add User" sits top-right, inside the header and
        // clear of the modal's own close button.
        title={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              paddingRight: 24,
            }}
          >
            <span>Assign Subscription</span>
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={handleOpenAddUserModal}
              disabled={assignSubmitting}
            >
              Add User
            </Button>
          </div>
        }
        open={assignModalOpen}
        onCancel={handleCloseAssignModal}
        onOk={() => assignForm.submit()}
        okText="Assign"
        confirmLoading={assignSubmitting}
        destroyOnClose
      >
        <Form
          form={assignForm}
          layout="vertical"
          onFinish={handleAssignSubmit}
        >
          <Form.Item
            label="User Email"
            name="user_id"
            rules={[{ required: true, message: 'Please select a user' }]}
          >
            <Select
              showSearch
              placeholder="Search by email"
              filterOption={false}
              onSearch={handleUserSearch}
              loading={userSearchLoading}
              options={userOptions}
              notFoundContent={userSearchLoading ? 'Searching…' : 'Type to search'}
              allowClear
              onChange={(value, option) => {
                const cid = option?.subscription_country_id;
                assignForm.setFieldsValue({
                  country_id: cid != null ? String(cid) : undefined,
                });
                const inActiveList =
                  cid != null &&
                  assignCountryOptions.some((o) => o.value === String(cid));
                setInheritedCountryOption(
                  cid != null && !inActiveList
                    ? {
                        label: `${option?.subscription_country_name || `Country #${cid}`} (inactive)`,
                        value: String(cid),
                      }
                    : null
                );
              }}
            />
          </Form.Item>
          <Form.Item
            label="Country"
            name="country_id"
            rules={[{ required: true, message: 'Please select a country' }]}
          >
            <Select
              placeholder="Auto-filled from user's billing country"
              options={
                inheritedCountryOption
                  ? [inheritedCountryOption, ...assignCountryOptions]
                  : assignCountryOptions
              }
              showSearch
              optionFilterProp="label"
              allowClear
            />
          </Form.Item>
          <Form.Item
            label="Subscription Plan"
            name="plan_id"
            rules={[{ required: true, message: 'Please select a plan' }]}
          >
            <Select
              placeholder="Select plan"
              options={assignPlanOptions}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item
            label="Plan Type"
            name="plan_type_id"
            rules={[{ required: true, message: 'Please select a plan type' }]}
          >
            <Select
              placeholder="Select plan type"
              options={assignPlanTypeOptions}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        </Form>
      </Modal>
      {/* Add User — stacked above the assign modal, which stays open underneath so
          the plan/type already chosen there is not lost. */}
      <Modal
        title="Add User"
        open={addUserModalOpen}
        onCancel={handleCloseAddUserModal}
        onOk={() => addUserForm.submit()}
        okText="Create User"
        confirmLoading={addUserSubmitting}
        destroyOnClose
        width={640}
        zIndex={1100}
      >
        <Form form={addUserForm} layout="vertical" onFinish={handleAddUserSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="First Name"
                name="user_firstname"
                rules={[{ required: true, message: 'Please enter a first name' }]}
              >
                <Input placeholder="First name" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Last Name"
                name="user_lastname"
                rules={[{ required: true, message: 'Please enter a last name' }]}
              >
                <Input placeholder="Last name" allowClear />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="Email"
            name="user_email"
            rules={[
              { required: true, message: 'Please enter an email' },
              { type: 'email', message: 'Enter a valid email' },
            ]}
          >
            <Input placeholder="name@example.com" allowClear />
          </Form.Item>
          <Row gutter={16}>
            <Col span={10}>
              <Form.Item
                label="Mobile Country Code"
                name="user_country_code"
                rules={[{ required: true, message: 'Please select a country code' }]}
              >
                <Select
                  placeholder="Select code"
                  options={mobileCodeOptions}
                  showSearch
                  optionFilterProp="label"
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={14}>
              <Form.Item
                label="Mobile Number"
                name="user_mobile"
                // The API stores the national digits and the dial code separately,
                // so one without the other is rejected there. Caught here too, to
                // say so before the round trip.
                dependencies={['user_country_code']}
                rules={[
                  { required: true, message: 'Please enter a mobile number' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const code = getFieldValue('user_country_code');
                      const num = (value || '').trim();
                      if (num && !code) {
                        return Promise.reject(new Error('Select a country code for this number'));
                      }
                      if (!num && code) {
                        return Promise.reject(new Error('Enter a mobile number for this country code'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input placeholder="National number, without the code" allowClear />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Date of Birth"
                name="user_dob"
                rules={[
                  { required: true, message: 'Please select a date of birth' },
                  {
                    // The picker greys these out, but a value can still arrive by
                    // typing into the field, so the rule is enforced here as well.
                    validator: (_, value) =>
                      isUnderageDob(value)
                        ? Promise.reject(
                            new Error(`User must be at least ${MIN_AGE_YEARS} years old`)
                          )
                        : Promise.resolve(),
                  },
                ]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD"
                  // Anything newer than the cutoff is under age — which also rules
                  // out future dates. The API applies the same rule.
                  disabledDate={isUnderageDob}
                  // Open on the cutoff month, not today. Every date in the current
                  // month is under age, so opening there shows a calendar with
                  // nothing selectable and eighteen years of clicking to get back.
                  defaultPickerValue={latestAllowedDob()}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Gender"
                name="user_gender"
                rules={[{ required: true, message: 'Please select a gender' }]}
              >
                <Select
                  placeholder="Select gender"
                  allowClear
                  options={[
                    { label: 'Male', value: 'male' },
                    { label: 'Female', value: 'female' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Modal
        title={`User Invoice`}
        open={invoiceModal.visible}
        onCancel={handleCloseInvoiceModal}
        footer={null}
        width={700}
      >
        {invoiceModal.loading ? (
          <div>Loading...</div>
        ) : invoiceModal.invoice ? (
          <div>
            {invoiceModal.invoice.status && invoiceModal.invoice.invoice_url ? (
              <>
                <Typography.Text strong>Invoice Number:</Typography.Text> {invoiceModal.invoice.invoice_number || 'N/A'}<br />
                <Typography.Text strong>Invoice URL:</Typography.Text> <a href={invoiceModal.invoice.invoice_url} target="_blank" rel="noopener noreferrer">Download</a><br />
                <Typography.Text strong>Message:</Typography.Text> {invoiceModal.invoice.message}<br />
              </>
            ) : (
              <Typography.Text type="secondary">{invoiceModal.invoice.message || 'Invoice not available'}</Typography.Text>
            )}
          </div>
        ) : (
          <Typography.Text type="secondary">No invoice data found.</Typography.Text>
        )}
      </Modal>
    </div>
  );
}

export default UserSubscription;